import { storeToRefs } from 'pinia';
import { ref, computed } from '@nuxtjs/composition-api';
import useEvents from '@/composable/various/useEvents';
import { useCategoriesStore } from '~/stores/useCategoriesStore';


export default function useCategories (hubbleClient) {
  const loading = ref(false)
  if (!hubbleClient) {
    throw new Error('You must provide the hubbleClient to use this composable');
  }
  const categoriesStore = useCategoriesStore()
  const {
    categories,
    allCategories,
    categoryTree,
    categoryPageInfo,
    offeredToCustomerType,
    hasSpecialPrices
  } = storeToRefs(categoriesStore)
  const { publishEvent } = useEvents(hubbleClient);
  const getCategories = async () => {
    if (allCategories.value.length) {
      return allCategories.value
    }
    loading.value = true
    const res = await hubbleClient.categories.getCategories()
    allCategories.value = res.data;
    loading.value = false
    return allCategories.value;
  }
  const getRootCategories = async () => {
    if (categories.value.length) {
      return categories.value
    }
    loading.value = true
    const res = await hubbleClient.categories.getRootCategories()
    categories.value = res.data;
    loading.value = false
    return categories.value;
  }

  const getCategoryBySlug = async (slug) => {
    loading.value = true
    if (!allCategories?.value?.length || allCategories?.value?.length < 1) {
      const res = await hubbleClient.categories.getCategories()
      allCategories.value = res.data;
      loading.value = false;
    } else {
      loading.value = false
    }
    const category = allCategories?.value?.find(category => category.slug.toLowerCase() === slug.toLowerCase());
    if (process.server && !category) {
      const message = 'NO Category Could be found for category SLUG, this is most likely because of wrong slug part of url url (shop/#slug#) or ' +
        'wrong category configuration in merchantcenter: slug received as url param is: ' + slug;
      await publishEvent('CT## ' + message);
    }
    return category
  }

  const getOfferedToCustomerTypeInfo = async ({ categorySlug = null, categoryId = null }) => {
    let category = null
    if (categorySlug !== null) {
      category = await getCategory({ categorySlug })
    } else {
      category = await getCategory({ categoryId })
    }
    offeredToCustomerType.value = category?.offeredToCustomerType
    return offeredToCustomerType.value
  }

  const getHasSpecialPrices = async ({ categorySlug = null, categoryId = null }) => {
    let category = null
    if (categorySlug !== null) {
      category = await getCategory({ categorySlug })
    } else {
      category = await getCategory({ categoryId })
    }
    hasSpecialPrices.value = category?.hasSpecialPrices || false
    return hasSpecialPrices.value
  }

  const getCategory = async ({ categorySlug = null, categoryId = null }) => {
    const categories = await getCategories();
    let category = null
    if (categorySlug !== null) {
      category = categories.find(category => category.slug === categorySlug);
    } else {
      category = categories.find(category => category.id === categoryId);
    }
    if (process.server && !category) {
      const message = 'NO Category Could be found for category ID, this is most likely because of wrong slug part of url url (shop/#slug#) or ' +
        'wrong category configuration in merchantcenter: slug received as url param is: ' + categoryId;
      await publishEvent('CT## ' + message);
    }
    return category
  }

  const getCategoryTree = async (categoryId) => {
    if (categoryTree.value.length) {
      return categoryTree.value
    }
    loading.value = true
    const res = await hubbleClient.categories.getCategoryTree(categoryId)
    categoryTree.value = res.data;
    loading.value = false
    return categoryTree.value;
  }
  const completeSetOfCategories = computed(() => {
    return allCategories.value;
  })
  const getCategoryNameById = (categoryId) => {
    return allCategories?.value?.find((cat) => cat.id === categoryId)
  }
  const getCategoryHeader = async (categorySlug) => {
    if (categoriesStore.categoryHeaders[categorySlug] && process.browser) {
      categoryPageInfo.value = categoriesStore.categoryHeaders[categorySlug]
      return categoryPageInfo.value
    }
    loading.value = true
    const res = await hubbleClient.categoryHeader.getCategoryHeader(categorySlug)
    categoriesStore.categoryHeaders[categorySlug] = res.data
    categoryPageInfo.value = categoriesStore.categoryHeaders[categorySlug]
    loading.value = false
    return categoryPageInfo.value;
  }
  const getCategorySlugById = (categoryId) => {
    return categories?.value?.find((cat) => cat.id === categoryId)?.slug
  }
  return {
    getCategory,
    hasSpecialPrices,
    getHasSpecialPrices,
    offeredToCustomerType,
    getOfferedToCustomerTypeInfo,
    getCategorySlugById,
    categoryTree,
    getRootCategories,
    getCategoryBySlug,
    getCategories,
    getCategoryTree,
    loading: computed(() => loading.value),
    navigationCategories: computed(() => {
      return categories.value;
    }),
    completeSetOfCategories,
    getCategoryNameById,
    getCategoryHeader,
    title: computed(() => categoryPageInfo?.value?.title),
    description: computed(() => categoryPageInfo?.value?.description),
    metaTitle: computed(() => categoryPageInfo?.value?.metaTitle),
    metaDescription: computed(() => categoryPageInfo?.value?.metaDescription),
    articleCards: computed(() => categoryPageInfo?.value?.promotionAndArticles),
    categoryFaQs: computed(() => {
      const faqList = categoryPageInfo?.value?.faqListWithTitle?.faqList?.map((val) => {
        return { heading: val.title, content: val.description, specificationList: val.productSpecificationList }
      })
      return { title: categoryPageInfo?.value?.faqListWithTitle?.title, faqList }
    })
  }
}
